/**
 * Operator Edit Modal
 * Popup form for editing an operator without leaving the list view
 */

import { useEffect, useState } from 'react'
import { operatorApi } from '../../services/operatorApi'
import OperatorForm from './OperatorForm'

function normalizeOperator(data) {
  if (!data) return null
  return data.operator || data
}

export default function OperatorEditModal({
  isOpen,
  operator,
  onClose,
  onSuccess,
}) {
  const [initialData, setInitialData] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = 'unset'
      return undefined
    }

    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !operator) {
      setInitialData(null)
      setError(null)
      return
    }

    setInitialData(normalizeOperator(operator))
    setError(null)
  }, [isOpen, operator])

  if (!isOpen) return null

  const handleSubmit = async (formData) => {
    const id = initialData?.id || operator?.id
    if (!id) return
    setError(null)
    try {
      await operatorApi.updateOperator(id, formData)
      onSuccess?.()
      onClose?.()
    } catch (err) {
      setError(err?.message || 'Failed to update operator')
      throw err
    }
  }

  const handleClose = () => {
    onClose?.()
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Edit Operator</h2>
              <p className="text-xs text-gray-400 mt-0.5">Update operator details</p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {initialData ? (
            <OperatorForm
              key={initialData.id}
              initialData={initialData}
              onSubmit={handleSubmit}
              onCancel={handleClose}
              isEdit={true}
            />
          ) : (
            <div className="py-6 text-center">
              <p className="text-sm text-gray-500 mb-3">No operator selected</p>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
